import { useEffect, useState } from "react";
import { api, fmtDateTime } from "@/lib/api";
import { PageHead } from "@/pages/AppLayout";
import { toast } from "sonner";
import { Gift, Copy, Check, Users, Ticket, Share2, MessageCircle, Mail } from "lucide-react";

const HOW = [
  { n: "01", t: "Share your link", d: "Send your unique link to other operators, mates and mechanics." },
  { n: "02", t: "They start a fleet", d: "When they sign up and create a fleet, their first month is free." },
  { n: "03", t: "You both win", d: "You earn a 1-month-free code too — apply it at your next billing." },
];

export default function Referrals() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState("");

  useEffect(() => { api.get("/referrals/me").then(({ data }) => setData(data)).catch(() => toast.error("Could not load referrals")); }, []);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key); toast.success("Copied");
      setTimeout(() => setCopied(""), 1800);
    } catch { toast.error("Copy failed — select and copy manually"); }
  };

  if (!data) return <div className="p-5 md:p-10" data-testid="referrals-page"><p className="font-head text-[#FFB800] uppercase tracking-widest">Loading…</p></div>;

  const link = data.ref_link;
  const shareText = `I use IronLog to keep my fleet running — services, pre-starts, work orders & rego reminders in one spot. Start a fleet and your first month's free:`;
  const enc = encodeURIComponent;
  const shares = [
    { label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${enc(shareText + " " + link)}`, testid: "share-whatsapp" },
    { label: "SMS", icon: MessageCircle, href: `sms:?&body=${enc(shareText + " " + link)}`, testid: "share-sms" },
    { label: "Email", icon: Mail, href: `mailto:?subject=${enc("Try IronLog — first month free")}&body=${enc(shareText + "\n\n" + link)}`, testid: "share-email" },
    { label: "Facebook", icon: Share2, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(link)}`, testid: "share-facebook" },
  ];

  return (
    <div className="p-5 md:p-10 pb-24" data-testid="referrals-page">
      <PageHead title="Refer & Earn" sub="Give a free month, get a free month." />

      {/* Hero */}
      <div className="card p-6 md:p-8 mb-6 border-t-4 border-[#FFB800]" data-testid="referral-hero">
        <div className="flex items-center gap-2 mb-2"><Gift className="w-6 h-6 text-[#FFB800]" /><span className="font-head font-800 text-xl uppercase tracking-tight">1 month free — for both of you</span></div>
        <p className="font-body text-[#94A3B8] max-w-2xl">Share IronLog with another operator. When they start a fleet, they get their first month free — and you earn a month free too. No limit on how many mates you refer.</p>

        <label className="il-label mt-6">Your referral link</label>
        <div className="flex flex-col sm:flex-row gap-2">
          <input readOnly value={link} className="il-input font-mono text-sm" data-testid="referral-link" onFocus={(e) => e.target.select()} />
          <button onClick={() => copy(link, "link")} className="btn-signal h-12 px-5 font-head flex items-center gap-2 shrink-0" data-testid="copy-referral-link">
            {copied === "link" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied === "link" ? "Copied" : "Copy Link"}
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {shares.map((s) => (
            <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="btn-ghost h-10 px-4 font-head text-sm flex items-center gap-2 border border-white/15" data-testid={s.testid}><s.icon className="w-4 h-4" /> {s.label}</a>
          ))}
        </div>
        <p className="font-mono text-xs text-[#94A3B8] mt-3">Or share your code: <span className="text-[#FFB800]">{data.ref_code}</span></p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6" data-testid="referral-stats">
        <div className="card p-5"><Users className="w-5 h-5 text-[#FFB800] mb-2" /><p className="font-mono text-3xl">{data.stats.signed_up}</p><p className="font-mono text-xs text-[#94A3B8] uppercase tracking-wide">Sign-ups</p></div>
        <div className="card p-5"><Gift className="w-5 h-5 text-[#FFB800] mb-2" /><p className="font-mono text-3xl">{data.stats.months_earned}</p><p className="font-mono text-xs text-[#94A3B8] uppercase tracking-wide">Months earned</p></div>
        <div className="card p-5"><Ticket className="w-5 h-5 text-[#00E676] mb-2" /><p className="font-mono text-3xl text-[#00E676]">{data.stats.months_available}</p><p className="font-mono text-xs text-[#94A3B8] uppercase tracking-wide">Ready to use</p></div>
      </div>

      {/* Rewards */}
      <div className="card mb-6" data-testid="referral-rewards">
        <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2"><Ticket className="w-5 h-5 text-[#FFB800]" /><h2 className="font-head font-700 text-lg uppercase tracking-wide">Your free-month codes</h2></div>
        {data.rewards.length === 0 ? (
          <p className="p-5 font-body text-sm text-[#94A3B8]">No codes yet — share your link above to start earning free months.</p>
        ) : (
          <div className="divide-y divide-white/10">
            {data.rewards.map((r) => (
              <div key={r.code} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`reward-${r.code}`}>
                <div className="min-w-0">
                  <p className="font-mono text-[#FFB800] text-lg">{r.code}</p>
                  <p className="font-mono text-[11px] text-[#94A3B8] truncate">{r.label} · {r.percent_off}% off</p>
                </div>
                {r.used
                  ? <span className="badge border border-white/15 text-[#94A3B8]">Used</span>
                  : <button onClick={() => copy(r.code, r.code)} className="btn-ghost h-9 px-3 font-head text-sm border border-white/15 flex items-center gap-2" data-testid={`copy-reward-${r.code}`}>{copied === r.code ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} Copy</button>}
              </div>
            ))}
          </div>
        )}
        <p className="px-5 py-3 font-mono text-[11px] text-[#94A3B8] border-t border-white/10">Apply a code in the discount box on the Billing page at your next upgrade or renewal.</p>
      </div>

      {/* How it works */}
      <div className="grid sm:grid-cols-3 gap-3 mb-6" data-testid="referral-how">
        {HOW.map((h) => (
          <div key={h.n} className="card p-5">
            <p className="font-mono text-[#FFB800] text-sm">{h.n}</p>
            <p className="font-head font-700 uppercase tracking-tight mt-1">{h.t}</p>
            <p className="font-body text-sm text-[#94A3B8] mt-1">{h.d}</p>
          </div>
        ))}
      </div>

      {/* Recent referrals */}
      {data.referrals.length > 0 && (
        <div className="card" data-testid="referral-list">
          <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2"><Users className="w-5 h-5 text-[#FFB800]" /><h2 className="font-head font-700 text-lg uppercase tracking-wide">Who you&apos;ve referred</h2></div>
          <div className="divide-y divide-white/10">
            {data.referrals.map((r, i) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between" data-testid={`referral-item-${i}`}>
                <p className="font-body text-white">{r.name}</p>
                <p className="font-mono text-[11px] text-[#94A3B8]">{fmtDateTime(r.created_at)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
